import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = ["intake", "prep", "review", "filed"];
const STAGE_LABELS = { intake: "Intake", prep: "Preparation", review: "Review", filed: "Filed" };

const PRIORITY_STYLES = {
  high: "bg-[#D32F2F]/10 text-[#D32F2F]",
  low: "bg-slate-200 text-slate-700",
  medium: "bg-amber-50 text-amber-700",
};

const priorityClass = (priority) => PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;

export default function Workflow() {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ title: "", stage: "intake", priority: "medium", due_date: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/workflow");
    setTasks(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const tasksByStage = useMemo(() => {
    const map = { intake: [], prep: [], review: [], filed: [] };
    for (const t of tasks) {
      if (map[t.stage]) map[t.stage].push(t);
    }
    return map;
  }, [tasks]);

  const submit = async () => {
    try {
      await api.post("/workflow", form);
      toast.success("Task added");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const move = async (task, stage) => {
    await api.patch(`/workflow/${task.id}`, { ...task, stage });
    load();
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/workflow/${toDelete.id}`);
      toast.success("Task deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="workflow-title">Workflow</h1>
          <p className="text-slate-600 mt-1">Kanban across Intake → Prep → Review → Filed.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="new-task-btn"><Plus size={16} className="mr-1" /> New task</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add workflow task</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Stage</Label>
                  <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#0A2540]" data-testid="task-submit">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAGES.map(stage => (
          <div key={stage} className="rounded-md border tf-hairline bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <div className="font-[Chivo] font-bold text-[#0A2540]">{STAGE_LABELS[stage]}</div>
              <span className="text-xs font-mono text-slate-500 bg-white rounded px-2 py-0.5 border tf-hairline">{tasksByStage[stage].length}</span>
            </div>
            <div className="mt-3 space-y-2" data-testid={`stage-${stage}`}>
              {tasksByStage[stage].map(t => (
                <div key={t.id} className="rounded-md bg-white border tf-hairline p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-[#0A2540] flex-1">{t.title}</div>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setToDelete(t)} data-testid={`delete-task-${t.id}`}>
                      <Trash size={12} className="text-[#D32F2F]" />
                    </Button>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className={`font-mono uppercase tracking-widest px-2 py-0.5 rounded ${priorityClass(t.priority)}`}>{t.priority}</span>
                    <span className="text-slate-500">{t.due_date}</span>
                  </div>
                  <div className="mt-3">
                    <Select value={t.stage} onValueChange={(v) => move(t, v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`move-task-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>Move to {STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && `"${toDelete.title}" · ${STAGE_LABELS[toDelete.stage] || toDelete.stage}`}. This permanently removes the task. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-task-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="delete-task-confirm">
              Delete task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
